import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { X, Upload, Check, AlertCircle, FileSpreadsheet, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_API_URL;

// Maps Excel workshop name (trimmed lowercase) → workshop ID
const WORKSHOP_NAME_MAP = {
    'coagulation insights': '1',
    'coagulation workshop': '1',
    'coagulation': '1',
    '"peak-a-boo"- detecting hemoglobinopathy by hplc': '2',
    'high performance liquid chromatography (hplc)': '2',
    'hplc': '2',
    'hands on flow cytometry in leukemia diagnosis: from sample to report': '3',
    'flow cytometry': '3',
    'immunohematology & apheresis: from technique to therapy': '4',
    'immunohematology': '4',
    'apheresis': '4',
    'procedural skills- bone marrow biopsy, core biopsy and fnac': '5',
    'procedural skills': '5',
    'a journey through the gut-interpretation of gastrointestinal biopsies': '6',
    'gastro intestinal (gi)': '6',
    'gi': '6',
    'understanding immunohistochemistry (ihc) -multiutility tool or double-edged sword?': '7',
    'immunohistochemistry (ihc)': '7',
    'ihc': '7',
    'utility of immunofluorescence in nephropathology & dermatopathology': '8',
    'immunofluorescence (if)': '8',
    'if': '8',
    'digital pathology in practice-from glass slides to ai-ready workflows': '9',
    'digital pathology': '9',
    'mastering ana-if on hep2 & primate liver as per new icap guidelines (2024-25)': '10',
    'ana-if': '10',
};

const WORKSHOP_LABELS = {
    '1': 'Workshop 1 – Coagulation Insights',
    '2': 'Workshop 2 – HPLC',
    '3': 'Workshop 3 – Flow Cytometry',
    '4': 'Workshop 4 – Immunohematology & Apheresis',
    '5': 'Workshop 5 – Procedural Skills',
    '6': 'Workshop 6 – GI Biopsies',
    '7': 'Workshop 7 – IHC',
    '8': 'Workshop 8 – Immunofluorescence',
    '9': 'Workshop 9 – Digital Pathology',
    '10': 'Workshop 10 – ANA-IF',
};

const COLUMN_MAP = {
    'full name': 'fullName',
    'email id': 'email',
    'email': 'email',
    'contact number': 'contactNumber',
    'whatsapp number': 'whatsappNumber',
    'designation': 'designation',
    'organisation': 'organization',
    'organization': 'organization',
    'city': 'city',
    'state': 'state',
    'country': 'country',
    'name of medical council': 'medical_council',
    'medical council number': 'medical_council_number',
    'speciality disipline': 'specialty',
    'speciality discipline': 'specialty',
    'specialty/discipline': 'specialty',
    'specialty': 'specialty',
    'registration category': 'registrationCategory',
    'kciamp membership number': 'categoryIdNumber',
    'kciapm membership number': 'categoryIdNumber',
    'membership number': 'categoryIdNumber',
    'workshop': 'workshopName',
    'conference reg id': 'conferenceRegId',
    'conference registration id': 'conferenceRegId',
};

const DISPLAY_LABELS = {
    fullName: 'Full Name',
    email: 'Email',
    contactNumber: 'Contact',
    registrationCategory: 'Category',
    selectedWorkshopId: 'Workshop ID',
    workshopName: 'Workshop (raw)',
    organization: 'Organization',
    categoryIdNumber: 'Membership No.',
};

const Steps = ({ current }) => {
    const steps = ['Upload File', 'Preview', 'Import'];
    return (
        <div className="flex items-center gap-0 mb-6">
            {steps.map((s, i) => (
                <React.Fragment key={i}>
                    <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                            i < current ? 'bg-indigo-600 border-indigo-600 text-white'
                            : i === current ? 'border-indigo-600 text-indigo-600 bg-white'
                            : 'border-gray-200 text-gray-400 bg-white'
                        }`}>
                            {i < current ? <Check className="w-3.5 h-3.5" /> : i + 1}
                        </div>
                        <span className={`text-xs font-semibold hidden sm:block ${i === current ? 'text-indigo-600' : i < current ? 'text-gray-600' : 'text-gray-300'}`}>{s}</span>
                    </div>
                    {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < current ? 'bg-indigo-600' : 'bg-gray-200'}`} />}
                </React.Fragment>
            ))}
        </div>
    );
};

const BulkRegisterWorkshopModal = ({ credentials, onClose, onImported }) => {
    const [step, setStep] = useState(0);
    const [file, setFile] = useState(null);
    const [parsedRows, setParsedRows] = useState([]);
    const [unmappedCols, setUnmappedCols] = useState([]);
    const [unmappedWorkshops, setUnmappedWorkshops] = useState([]);
    const [dragging, setDragging] = useState(false);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);
    const fileRef = useRef();

    const parseFile = useCallback((f) => {
        setFile(f);
        const reader = new FileReader();
        reader.onload = (e) => {
            const wb = XLSX.read(e.target.result, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
            if (rawRows.length === 0) return;

            // Find the actual header row (first row with >= 3 non-empty string cells)
            let headerRowIdx = 0;
            for (let i = 0; i < Math.min(5, rawRows.length); i++) {
                const nonEmpty = rawRows[i].filter(v => typeof v === 'string' && v.trim().length > 0);
                if (nonEmpty.length >= 3) { headerRowIdx = i; break; }
            }
            const headers = rawRows[headerRowIdx];
            const dataRows = rawRows.slice(headerRowIdx + 1);

            const mapped = [];
            const unmapped = new Set();
            const badWorkshops = new Set();

            for (const row of dataRows) {
                if (row.every(v => v === '' || v === null || v === undefined)) continue;

                const mappedRow = {};
                headers.forEach((col, idx) => {
                    const key = COLUMN_MAP[col.toString().trim().toLowerCase()];
                    const val = row[idx];
                    if (key) {
                        mappedRow[key] = val;
                    } else {
                        const trimmed = col.toString().trim();
                        if (trimmed && trimmed !== 'Sl. No' && !trimmed.startsWith('__EMPTY') && trimmed !== '') {
                            unmapped.add(trimmed);
                        }
                    }
                });

                // Resolve workshop name → ID
                if (mappedRow.workshopName) {
                    const wId = WORKSHOP_NAME_MAP[mappedRow.workshopName.toString().trim().toLowerCase()];
                    if (wId) {
                        mappedRow.selectedWorkshopId = wId;
                    } else {
                        badWorkshops.add(mappedRow.workshopName.toString().trim());
                    }
                }

                if (mappedRow.fullName || mappedRow.email) {
                    mapped.push(mappedRow);
                }
            }

            setParsedRows(mapped);
            setUnmappedCols([...unmapped]);
            setUnmappedWorkshops([...badWorkshops]);
            setStep(1);
        };
        reader.readAsArrayBuffer(f);
    }, []);

    const handleDrop = (e) => {
        e.preventDefault(); setDragging(false);
        const f = e.dataTransfer.files[0];
        if (f) parseFile(f);
    };

    const handleFileInput = (e) => {
        const f = e.target.files[0];
        if (f) parseFile(f);
    };

    const rowsWithWorkshop = parsedRows.filter(r => r.selectedWorkshopId);
    const rowsWithoutWorkshop = parsedRows.filter(r => !r.selectedWorkshopId);

    // Workshop distribution summary
    const workshopDist = rowsWithWorkshop.reduce((acc, r) => {
        acc[r.selectedWorkshopId] = (acc[r.selectedWorkshopId] || 0) + 1;
        return acc;
    }, {});

    const runImport = async () => {
        setImporting(true);
        try {
            const res = await fetch(`${API}/api/custom-events/admin/workshop/participants/bulk-register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...credentials, participants: rowsWithWorkshop }),
            });
            const data = await res.json();
            setResult(data);
            setStep(2);
            if (data.success && onImported) onImported();
        } catch (err) {
            setResult({ success: false, message: err.message });
            setStep(2);
        } finally {
            setImporting(false);
        }
    };

    const previewFields = ['fullName', 'email', 'contactNumber', 'registrationCategory', 'selectedWorkshopId', 'organization'];

    const StepUpload = () => (
        <div>
            <div
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current.click()}
                className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-colors ${
                    dragging ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                }`}
            >
                <FileSpreadsheet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm font-semibold text-gray-700 mb-1">Drop your Excel file here</p>
                <p className="text-xs text-gray-400">or click to browse · .xlsx, .xls supported</p>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileInput} />
            </div>
            <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-700">
                <p className="font-semibold mb-1">Workshop bulk-upload rules:</p>
                <ul className="list-disc list-inside space-y-0.5">
                    <li>Participants are marked as <strong>bulk-admin</strong> and will NOT count toward workshop slot limits</li>
                    <li>All rows start with <strong>isApproved: false</strong> — manual approval required</li>
                    <li>Amount is set to <strong>₹0</strong></li>
                    <li>Duplicate emails are skipped automatically</li>
                </ul>
            </div>
        </div>
    );

    const StepPreview = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black text-indigo-700">{parsedRows.length}</p>
                    <p className="text-[10px] text-indigo-500 font-semibold uppercase">Rows Detected</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black text-emerald-700">{rowsWithWorkshop.length}</p>
                    <p className="text-[10px] text-emerald-500 font-semibold uppercase">Ready to Import</p>
                </div>
                <div className={`border rounded-xl p-3 text-center ${rowsWithoutWorkshop.length > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
                    <p className={`text-2xl font-black ${rowsWithoutWorkshop.length > 0 ? 'text-red-600' : 'text-gray-400'}`}>{rowsWithoutWorkshop.length}</p>
                    <p className={`text-[10px] font-semibold uppercase ${rowsWithoutWorkshop.length > 0 ? 'text-red-400' : 'text-gray-400'}`}>No Workshop ID</p>
                </div>
            </div>

            {unmappedWorkshops.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-xs font-semibold text-red-800">Unrecognised workshop names (rows skipped):</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                            {unmappedWorkshops.map(w => (
                                <span key={w} className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-md">{w}</span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {unmappedCols.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-xs font-semibold text-amber-800">Unrecognised columns (skipped):</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                            {unmappedCols.map(c => (
                                <span key={c} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-md">{c}</span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Workshop distribution */}
            {Object.keys(workshopDist).length > 0 && (
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-3">
                    <p className="text-xs font-semibold text-gray-600 mb-2">Workshop Distribution</p>
                    <div className="flex flex-wrap gap-1.5">
                        {Object.entries(workshopDist).sort((a, b) => Number(a[0]) - Number(b[0])).map(([id, count]) => (
                            <span key={id} className="inline-flex items-center gap-1.5 px-2 py-1 bg-white border border-indigo-100 rounded-lg text-xs font-semibold text-indigo-700">
                                <span className="w-4 h-4 bg-indigo-100 rounded-full text-[9px] flex items-center justify-center font-black">{id}</span>
                                {count} pax
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-xl px-4 py-2.5 space-y-0.5">
                <p><span className="font-semibold text-gray-700">Registration Source:</span> bulk-admin (excluded from slot counts)</p>
                <p><span className="font-semibold text-gray-700">Amount:</span> ₹0</p>
                <p><span className="font-semibold text-gray-700">Is Approved:</span> false — requires manual approval</p>
                <p><span className="font-semibold text-gray-700">Payment Status:</span> completed</p>
            </div>

            <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">Preview (first 5 rows)</p>
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table className="w-full text-xs">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                {previewFields.map(f => (
                                    <th key={f} className="px-3 py-2 text-left font-semibold text-gray-500 whitespace-nowrap">
                                        {DISPLAY_LABELS[f] || f}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {rowsWithWorkshop.slice(0, 5).map((row, i) => (
                                <tr key={i} className="hover:bg-gray-50">
                                    {previewFields.map(f => (
                                        <td key={f} className="px-3 py-2 text-gray-700 max-w-[140px] truncate">
                                            {f === 'selectedWorkshopId'
                                                ? <span className="font-bold text-indigo-600">{WORKSHOP_LABELS[row[f]] || row[f]}</span>
                                                : String(row[f] ?? '')}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const StepResult = () => {
        if (importing) return (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
                <RefreshCw className="w-10 h-10 text-indigo-500 animate-spin" />
                <p className="text-sm font-semibold text-gray-600">Registering participants...</p>
            </div>
        );
        if (!result) return null;
        return (
            <div className="space-y-4">
                <div className={`rounded-2xl p-5 text-center ${result.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                    {result.success ? (
                        <>
                            <Check className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                            <p className="text-xl font-black text-emerald-700">{result.created} registered</p>
                            <p className="text-sm text-emerald-600 mt-1">of {result.total} rows · {result.skipped} skipped</p>
                        </>
                    ) : (
                        <>
                            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
                            <p className="text-sm font-bold text-red-700">{result.message || 'Import failed'}</p>
                        </>
                    )}
                </div>
                {result.errors?.length > 0 && (
                    <div className="bg-red-50 border border-red-100 rounded-xl p-3">
                        <p className="text-xs font-bold text-red-700 mb-1">{result.errors.length} rows had issues:</p>
                        <div className="space-y-0.5 max-h-36 overflow-y-auto">
                            {result.errors.map((e, i) => (
                                <p key={i} className="text-[10px] text-red-600">Row {e.row}: {e.error}</p>
                            ))}
                        </div>
                    </div>
                )}
                <button onClick={onClose} className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-xl transition-colors">
                    Done
                </button>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                            <Upload className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                            <h2 className="text-sm font-bold text-gray-900">Bulk Register Workshop Participants</h2>
                            <p className="text-[10px] text-gray-400">KAPCON 2026 · Workshop · bulk-admin source</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5">
                    <Steps current={step} />
                    {step === 0 && <StepUpload />}
                    {step === 1 && <StepPreview />}
                    {step === 2 && <StepResult />}
                </div>

                {step < 2 && (
                    <div className="px-6 py-4 border-t border-gray-100 flex justify-between">
                        <button
                            onClick={() => setStep(s => Math.max(0, s - 1))}
                            disabled={step === 0}
                            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 disabled:opacity-30 transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4" /> Back
                        </button>
                        {step === 1 ? (
                            <button
                                onClick={runImport}
                                disabled={importing || rowsWithWorkshop.length === 0}
                                className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl disabled:opacity-50 transition-colors"
                            >
                                {importing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                Register {rowsWithWorkshop.length} Participants
                            </button>
                        ) : (
                            <button
                                onClick={() => setStep(s => s + 1)}
                                disabled={step === 0 || parsedRows.length === 0}
                                className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl disabled:opacity-50 transition-colors"
                            >
                                Continue <ChevronRight className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkRegisterWorkshopModal;
